import { useUpdateOnboardingInfoMutation } from '@/app/redux/endpoints/user';
import { useAppDispatch } from '@/app/redux/hooks';
import { useUser } from '@/app/redux/hooks/user';
import { setClaims } from '@/app/redux/slices/sessionSlice';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { handleLogout, refreshUserClaims } from '@/common/utils/auth';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import {
  InfoIcon,
  SignOutIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { Label } from '@shadcn/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import logo from '/ui/new-spellcaster-logo.svg';

const numStudentsOptions = ['1-40', '41-150', '151-400', '400+'];
const roleOptions = [
  'Teacher',
  'Parent / Guardian',
  'Speech Therapist',
  'Education Support',
  'Literacy Interventionist',
  'Other',
];
const referrerOptions = [
  'Internet Search',
  'Word of Mouth',
  'Professional Recommendation',
  'Facebook',
  'Instagram',
  'Other',
];

export default function Onboarding() {
  const dispatch = useAppDispatch();

  const { user } = useUser();
  const userId = user?.uid;
  const userName = user?.name;
  const userEmail = user?.email;

  const [updateOnboardingInfo] = useUpdateOnboardingInfoMutation();

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<JSX.Element | string | null>(null);

  const [organisationName, setOrganisationName] = useState('');
  const [numStudents, setNumStudents] = useState(numStudentsOptions[0]);
  const [role, setRole] = useState(roleOptions[0]);
  const [roleOther, setRoleOther] = useState('');
  const [referrer, setReferrer] = useState(referrerOptions[0]);
  const [referrerOther, setReferrerOther] = useState('');

  useEffect(() => {
    setError(null);
  }, []);

  async function handleSubmit() {
    if (!organisationName) {
      setError('Please enter a name for your organisation');
      return;
    }

    if (!userId) {
      console.error('No user ID found');
      setError('An error occurred. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const roleValue = role === 'Other' ? roleOther : role;
      const referrerValue = referrer === 'Other' ? referrerOther : referrer;

      const newOrganisationId = await handleCreateOrganisation();

      if (!newOrganisationId) {
        throw new Error('Failed to create organisation');
      }

      trackMixpanelEvent('Onboarding Complete', {
        organisationId: newOrganisationId,
        onboardingInfo: {
          role: roleValue,
          referrer: referrerValue,
          numStudents,
        },
      });

      await api.asEducator.sendWelcomeEmail({ uid: userId });

      await updateOnboardingInfo({
        userId,
        completed: false,
        onboardingInfo: {
          role: roleValue,
          referrer: referrerValue,
          numStudents,
        },
      }).unwrap();
    } catch (err) {
      console.error(err);
      setError(UNKNOWN_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateOrganisation() {
    if (!userId || !userEmail || !userName) {
      throw new Error('Missing user details');
    }

    const { stripeCustomerId } = await api.asEducator.createStripeCustomer({
      email: userEmail,
      name: organisationName,
    });

    const { organisationId } = await api.asEducator.createOrganisation({
      organisationName,
      stripeCustomerId,
    });

    const claims = await refreshUserClaims(
      (claims) =>
        !!claims &&
        Array.isArray(claims.organisationIds) &&
        claims.organisationIds.includes(organisationId),
    );
    if (claims) {
      dispatch(setClaims(claims));
    }

    return organisationId;
  }

  return (
    <OnboardingContainer>
      <OnboardingHeader>
        <img src={logo} alt="Spellcaster" />

        <Button
          className="flex items-center gap-1"
          variant="outline"
          size="sm"
          onClick={handleLogout}
        >
          <SignOutIcon size={16} />
          Sign Out
        </Button>
      </OnboardingHeader>
      <OnboardingContent>
        <OnboardingTitleContainer>
          <OnboardingTitle>Welcome to Spellcaster!</OnboardingTitle>
          <OnboardingPageIndicator>
            <OnboardingPageIndicatorStep active />
          </OnboardingPageIndicator>
          <OnboardingSubtitle>
            Tell us a bit about yourself to get started...
          </OnboardingSubtitle>
        </OnboardingTitleContainer>

        <OnboardingForm>
          {error && (
            <Alert variant="destructive">
              <WarningCircleIcon size={18} weight="bold" className="ml-1" />
              <span>{error}</span>
            </Alert>
          )}
          <InputContainer>
            <Label htmlFor="orgName">What is your organisation called?</Label>
            <span className="text-xs text-muted-foreground flex items-center gap-2 ml-1">
              <InfoIcon size={16} />
              We'll use this name to refer to you throughout the app, so enter
              the actual name of your school, business, or family.
            </span>
            <Input
              id="orgName"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
              placeholder="e.g. St. Mary's Primary School, Smith Family"
              maxLength={64}
            />
          </InputContainer>

          <InputContainer>
            <Label htmlFor="numStudents">How many learners do you have?</Label>
            <Select value={numStudents} onValueChange={setNumStudents}>
              <SelectTrigger id="numStudents">
                <SelectValue placeholder={numStudents} />
              </SelectTrigger>
              <SelectContent>
                {numStudentsOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </InputContainer>

          <InputContainer>
            <Label htmlFor="role">What is your role?</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger id="role">
                <SelectValue placeholder={role} />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {role === 'Other' && (
              <Input
                value={roleOther}
                onChange={(e) => setRoleOther(e.target.value)}
                placeholder="Please specify"
              />
            )}
          </InputContainer>

          <InputContainer>
            <Label htmlFor="referrer">
              How did you hear about Spellcaster?
            </Label>
            <Select value={referrer} onValueChange={setReferrer}>
              <SelectTrigger id="referrer">
                <SelectValue placeholder={referrer} />
              </SelectTrigger>
              <SelectContent>
                {referrerOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {referrer === 'Other' && (
              <Input
                value={referrerOther}
                onChange={(e) => setReferrerOther(e.target.value)}
                placeholder="Please specify"
              />
            )}
          </InputContainer>

          <ButtonContainer>
            <Button onClick={handleSubmit} loading={isLoading}>
              Continue
            </Button>
          </ButtonContainer>
        </OnboardingForm>
      </OnboardingContent>
    </OnboardingContainer>
  );
}

const OnboardingContainer = styled.div`
  height: 100vh;
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;

  padding: 24px 30px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const OnboardingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  img {
    width: 100%;
    max-width: 250px;
    margin-left: -16px;
  }
`;

const OnboardingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;

  width: 40%;
  @media (max-width: 1024px) {
    width: 60%;
  }
  @media (max-width: 768px) {
    width: 80%;
  }
`;

const OnboardingTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: 16px;
`;

const OnboardingPageIndicator = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0;
`;

const OnboardingPageIndicatorStep = styled.div<{ active?: boolean }>`
  width: 60px;
  height: 8px;
  border-radius: 20px;
  background-color: ${({ active }) =>
    active ? 'hsl(var(--primary))' : 'hsl(0 0% 90%)'};
`;

const OnboardingTitle = styled.p`
  font-size: 34px;
  font-weight: 600;
  color: hsl(var(--primary));
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const OnboardingSubtitle = styled.p`
  font-size: 18px;
  width: 100%;
  color: hsl(0 0% 32.2%);

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const OnboardingForm = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
  margin-bottom: auto;
  width: 100%;
  gap: 24px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;

  div {
    display: flex;
    gap: 8px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: center;
    gap: 8px;

    div {
      width: 100%;
      justify-content: center;
    }
  }
`;
